import { Localized } from "@fluent/react"
import { Alert, Autocomplete, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Skeleton, TextField } from "@mui/material"
import { useCallback, useMemo, useState } from "react"
import { AppId, AppTextFieldHandler } from "../../../app/appTypes"
import { useAppDispatch, useAppSelector } from "../../../app/hooks"
import { selectAllUsers, selectUserByName, User } from "../../users/usersSlice"
import { ManagedTeamMember, ManagementRoleType } from "../../utils/managedObject"
import { store } from "../../../app/store"
import { showError, showSuccess } from "../../notifications/notificationsSlice"
import { If } from "../../../app/If"
import { LoadUsers } from "../../../app/AppDataLoader"
import { OfferTeamMemberRoleMenuField } from "./OfferTeamMemberRoleMenuField"
import { LoadingButton } from "@mui/lab"
import { addOfferTeamMember } from "../ursOfferSlice"

export interface AddEditOfferTeamMemberDialogProps {
    offerId: AppId
    teamMembers: ManagedTeamMember[]
    mode: "create" | "edit" | "substitute"
    userToSubstitute: string
    onSuccessfulSave: (teamMember: ManagedTeamMember) => void
    onCancelClick: () => void,
}
export const AddEditOfferTeamMemberDialog = ({ offerId, teamMembers, mode, userToSubstitute, onCancelClick, onSuccessfulSave }: AddEditOfferTeamMemberDialogProps) => {
    const dispatch = useAppDispatch()
    const allUsers = useAppSelector(selectAllUsers)
    const [user, setUser] = useState<User | null>(() => mode != "substitute" ? (selectUserByName(store.getState(), userToSubstitute) ?? null) : null)
    const [role, setRole] = useState<ManagementRoleType>()
    const [saving, setSaving] = useState(false)
    const [serverError, setServerError] = useState("")
    const filteredUsers = allUsers.filter(
        user => !teamMembers.some(member => member.userName === user.userName)
      );

    const handleUserChange = useCallback((_, newUser) => {
        setUser(newUser)
    }, [])

    const handleUserRoleChange: AppTextFieldHandler = useCallback(e => setRole(e.target.value as ManagementRoleType), [setRole])


    const handleOkClick = async () => {
        if (offerId && user && role) {
            setSaving(true)
            try {
                switch (mode) {
                    case "create":

                        const result = await dispatch(addOfferTeamMember({ offerId: offerId, userName: user.userName, role: role }))
                        if (addOfferTeamMember.fulfilled.match(result)) {
                            const newTeamMember = result.payload as ManagedTeamMember;
                            dispatch(showSuccess("saved"))
                            onSuccessfulSave(newTeamMember)
                        } else {
                            let errorMsg = "error"
                            if (result.payload && result.payload.kind === 'http') {
                                const problem = result.payload.problem
                                if (problem) {
                                    errorMsg = problem.title

                                }
                            }
                            setServerError(errorMsg)
                        }

                        break
                    case "edit":
                        break
                
                }
            }
            catch (error) {
                dispatch(showError("error"))
            }
            finally {
                setSaving(false)
            }

        }
        setSaving(false)
    }

    return <>
        <Dialog key="addEditTeamMemberDialog" open={true} maxWidth="md" fullWidth keepMounted={false}>
            <DialogTitle>
                <If condition={mode === "create"}>
                    <Localized id="document-add-team-member">
                        <span>Nowy członek zespołu</span>
                    </Localized>
                </If>
                <If condition={mode === "edit"}>
                    <Localized id="document-edit-team-member">
                        <span>Edycja członka zespołu</span>
                    </Localized>
                </If>
                <If condition={mode === "substitute"}>
                    <Localized id="document-appoint-substitution" vars={{userName: userToSubstitute}}>
                        <span>Wyznacz zastępstwo</span>
                    </Localized>
                </If>
            </DialogTitle>
            <DialogContent sx={{
                padding: 2,
            }}>
                <LoadUsers component={<Skeleton />}>
                    <Autocomplete
                        sx={{
                            marginTop: 1,
                        }}
                        options={filteredUsers}
                        value={user}
                        onChange={handleUserChange}
                        getOptionLabel={({ firstname, surname, email }) => `${firstname} ${surname} (${email}))`}
                        renderInput={params =>
                            <TextField {...params}
                                helperText={user?.position ?? " "}
                                label={
                                    <Localized id="username">
                                        <span>Nazwa użytkownika</span>
                                    </Localized>
                                }
                            />
                        }
                    />
                </LoadUsers>
                <OfferTeamMemberRoleMenuField
                    value={role}
                    onChange={handleUserRoleChange}
                />
                {
                    serverError && <Alert sx={{ marginTop: 1, }} onClose={() => { setServerError("") }} severity="error">
                        <Localized id={serverError}>
                            <span>Server error</span>
                        </Localized>
                    </Alert>
                }
            </DialogContent>
            <DialogActions>
                <LoadingButton
                    loading={saving}
                    color="secondary"
                    variant="contained"
                    loadingIndicator={<Localized id="saving" />}
                    onClick={handleOkClick}>
                    <Localized id="save">
                        <span>save</span>
                    </Localized>
                </LoadingButton>
                <LoadingButton
                    color="primary"
                    variant="outlined"
                    loading={saving}
                    onClick={onCancelClick}>
                    <Localized id="cancel">
                        <span>Anuluj</span>
                    </Localized>
                </LoadingButton>
            </DialogActions>
        </Dialog>
    </>
}
